import 'moment-timezone';

import * as moment from 'moment';

export const dateTimeFormat = 'YYYY-MM-DDTHH:mm:ssZ';

export const convertTime12to24 = time12h => {
	const [time, modifier] = time12h.split(' ');

	let [hours, minutes] = time.split(':');

	if (hours === '12') {
		hours = '00';
	}

	if (modifier === 'PM') {
		hours = parseInt(hours, 10) + 12;
	}

	return `${hours}:${minutes}`;
};

export const getTimezone = offset => {
	const usTimezonesWithOffsets = moment.tz.zonesForCountry('US', true);
	const zones = usTimezonesWithOffsets.filter(zone => {
		return zone.offset == Math.abs(offset);
	});

	return zones[0];
};

export const getTimezoneAbbreviation = timezone => {
	const timezoneAbbreviation = moment.tz(moment(), timezone).format('z');

	return timezoneAbbreviation;
};

const setDateTimezoneData = date => {
	const parsedDate = moment.parseZone(date, dateTimeFormat);
	const parsedDateTimezoneOffset = parsedDate.utcOffset();
	const timezone = getTimezone(parsedDateTimezoneOffset);
	const dateWithTimezoneData = moment.tz(
		parsedDate.format(),
		timezone?.name || 'America/New_York'
	);

	return dateWithTimezoneData;
};

export const getFormattedTimezone = date => {
	const dateWithTimezoneData = setDateTimezoneData(date);
	const formattedTimezone = dateWithTimezoneData.format('z');

	return formattedTimezone;
};

export const getTimezoneFormattedDate = date => {
	const dateWithTimezoneData = setDateTimezoneData(date);
	const timezoneFormattedDate = dateWithTimezoneData.format('MM/DD/YY h:mmA (z)');

	return timezoneFormattedDate;
};

export const getAllDatesInRange = (dateRange: any) => {
	const datesInRange = [];
	const startDate = moment(dateRange.startDate);
	const endDate = moment(dateRange.endDate);
	let currentDate = moment(startDate);
	let isSameDate =
		currentDate.isSame(endDate, 'month') &&
		currentDate.isSame(endDate, 'day') &&
		currentDate.isSame(endDate, 'year');

	datesInRange.push(startDate);

	while (!isSameDate) {
		const nextDate = moment(currentDate.add(1, 'days'));
		if (
			nextDate.isSame(endDate, 'month') &&
			nextDate.isSame(endDate, 'day') &&
			nextDate.isSame(endDate, 'year')
		) {
			break;
		}

		datesInRange.push(nextDate);

		isSameDate =
			currentDate.isSame(endDate, 'month') &&
			currentDate.isSame(endDate, 'day') &&
			currentDate.isSame(endDate, 'year');
	}

	if (!isSameDate) {
		datesInRange.push(endDate);
	}

	return datesInRange;
};

export const getDateRangeWeekdays = (dates: any) => {
	const datesInRange = getAllDatesInRange(dates);

	const datesWithWeekdays = datesInRange.map(date => {
		return {
			date: date,
			weekday: date.day(),
		};
	});

	return datesWithWeekdays;
};

export const getSimpleDateRange = (selectedDate, days) => {
	const startDate = moment(selectedDate);
	const endDate = moment(selectedDate).add(days - 1, 'days'); //if same day should be 0

	return {
		startDate,
		endDate,
	};
};

/*
 **-------------------------------------------------------------------------------------
 ** METHOD NAME - setReservationDates
 **-------------------------------------------------------------------------------------
 */
export const setReservationDates = ({ reservationTimezone, startDate, endDate, createTime }) => {
	const reservationTimezoneAbbreviation = `(${getTimezoneAbbreviation(reservationTimezone)})`;
	const formattedStartDate = moment
		.tz(startDate, dateTimeFormat, reservationTimezone)
		.format('MM/DD/YYYY h:mm A');
	const formattedEndDate = moment
		.tz(endDate, dateTimeFormat, reservationTimezone)
		.format('MM/DD/YYYY h:mm A');
	const reservationCreateDate = moment
		.tz(createTime, dateTimeFormat, reservationTimezone)
		.format('MM/DD/YYYY h:mm A');

	return {
		startDate: formattedStartDate,
		endDate: formattedEndDate,
		reservationCreateDate: reservationCreateDate,
		datesAsString: `${formattedStartDate} - ${formattedEndDate} ${reservationTimezoneAbbreviation}`,
	};
};
/*
 **-------------------------------------------------------------------------------------
 ** METHOD NAME - formatDates
 **-------------------------------------------------------------------------------------
 */
export const formatDates: any = date => {
	return {
		startDate: moment
			.tz(moment(date).startOf('day').format('YYYY-MM-DD HH:mm:ss'), 'America/New_York')
			.format(dateTimeFormat),
		endDate: moment
			.tz(moment(date).endOf('day').format('YYYY-MM-DD HH:mm:ss'), 'America/New_York')
			.format(dateTimeFormat),
	};
};
/*
 **-------------------------------------------------------------------------------------
 ** METHOD NAME - formatTime
 **-------------------------------------------------------------------------------------
 */
export const formatTime = (time, timezone) => {
	const formattedTime = moment
		.tz(`${moment().format('MM/DD/YY')} ${time}`, 'MM/DD/YY hh:mm', timezone)
		.format('h:mm A');

	return formattedTime;
};

export const formatExpirationDate = expirationDate =>
	parseInt(expirationDate.split('/').pop() + expirationDate.split('/')[0]);

export const currentTimeStamp = () => new Date().getTime();
